import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import PropTypes from 'prop-types';

const SenetPrintDialog = ({ visible, onHide, paymentDetails, onConfirm }) => {
    const [includeGuarantor, setIncludeGuarantor] = useState(true);
    const [printType, setPrintType] = useState('all');
    const [selectedPartialInstallments, setSelectedPartialInstallments] = useState([]);
    const [partialInstallmentOptions, setPartialInstallmentOptions] = useState([]);

    useEffect(() => {
        if (paymentDetails && paymentDetails.length) {
            const partialPayments = paymentDetails.filter(item =>
                item.isPartialPayment && item.isPartialPayment.id === 1
            );

            // Create unique installment order options
            const uniqueInstallments = new Map();

            partialPayments.forEach(payment => {
                const key = payment.installmentOrder;
                if (!uniqueInstallments.has(key)) {
                    uniqueInstallments.set(key, {
                        installmentOrder: payment.installmentOrder,
                        label: `Taksit ${payment.installmentOrder}`
                    });
                }
            });

            setPartialInstallmentOptions(Array.from(uniqueInstallments.values()));
        }
    }, [paymentDetails]);

    // Form alanlarını sıfırlama fonksiyonu
    const resetForm = () => {
        setIncludeGuarantor(true);
        setPrintType('all');
        setSelectedPartialInstallments([]);
    };

    // onHide fonksiyonunu wrap edip form reset ekliyoruz
    const handleHide = () => {
        resetForm();
        onHide();
    };

    const handleConfirm = () => {
        onConfirm({
            includeGuarantor,
            printType,
            selectedPartialInstallments
        });
        resetForm();
    };

    const dialogFooter = (
        <div className="flex justify-end gap-3 mt-3 mb-3">
            <Button
                label="İptal"
                icon="pi pi-times"
                onClick={handleHide}
                className="rounded-xl shadow-md cursor-pointer border bg-transparent text-red-700 dark:text-white border-red-700 hover:border-red-500 hover:bg-red-500 hover:text-white dark:bg-red-600 dark:border-red-600 dark:hover:bg-red-700 dark:hover:border-red-700 dark:shadow-lg p-2 transition-all duration-200"
            />
            <Button
                label="Yazdır"
                icon="pi pi-print"
                onClick={handleConfirm}
                autoFocus
                className="rounded-xl shadow-md cursor-pointer border bg-transparent text-blue-700 dark:text-white border-blue-700 hover:border-blue-500 hover:bg-blue-500 hover:text-white dark:bg-blue-600 dark:border-blue-600 dark:hover:bg-blue-700 dark:hover:border-blue-700 dark:shadow-lg p-2 transition-all duration-200"
            />
        </div>
    );

    return (
        <Dialog
            header="Senet Yazdırma Seçenekleri"
            visible={visible}
            style={{ width: '550px' }}
            onHide={handleHide}
            footer={dialogFooter}
            className="border-round-xl shadow-lg"
            contentClassName="p-4 bg-white dark:bg-gray-800"
            headerClassName="text-gray-800 dark:text-white bg-gray-100 dark:bg-gray-700 border-round-top-xl"
            maskClassName="backdrop-filter backdrop-blur-sm"
        >
            <div className="p-fluid">
                <div className="field mb-5 p-2 bg-gray-50 dark:bg-gray-700 rounded-xl shadow-sm">
                    <div className="field-checkbox flex align-items-center">
                        <Checkbox
                            inputId="includeGuarantor"
                            checked={includeGuarantor}
                            onChange={(e) => setIncludeGuarantor(e.checked)}
                            className="mr-2"
                        />
                        <label htmlFor="includeGuarantor" className="ml-2 font-semibold text-gray-700 dark:text-white">
                            Kefil Bilgilerini Dahil Et
                        </label>
                    </div>
                </div>

                <div className="field mb-5 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl shadow-sm">
                    <label className="font-bold block mb-3 text-gray-800 dark:text-white border-bottom-1 border-gray-300 dark:border-gray-600 pb-2">
                        Yazdırılacak Senetler
                    </label>

                    <div className="field-radiobutton mb-3 flex align-items-center">
                        <RadioButton
                            inputId="all"
                            name="printType"
                            value="all"
                            onChange={(e) => setPrintType(e.value)}
                            checked={printType === 'all'}
                            className="mr-2"
                        />
                        <label htmlFor="all" className="ml-2 text-gray-700 dark:text-gray-100 font-medium">
                            Tüm Senetleri Yazdır
                        </label>
                    </div>

                    <div className="field-radiobutton mb-3 flex align-items-center">
                        <RadioButton
                            inputId="normal"
                            name="printType"
                            value="normal"
                            onChange={(e) => setPrintType(e.value)}
                            checked={printType === 'normal'}
                            className="mr-2"
                        />
                        <label htmlFor="normal" className="ml-2 text-gray-700 dark:text-gray-100 font-medium">
                            Normal Senetleri Yazdır
                        </label>
                    </div>

                    <div className="field-radiobutton flex align-items-center">
                        <RadioButton
                            inputId="partial"
                            name="printType"
                            value="partial"
                            onChange={(e) => setPrintType(e.value)}
                            checked={printType === 'partial'}
                            className="mr-2"
                        />
                        <label htmlFor="partial" className="ml-2 text-gray-700 dark:text-gray-100 font-medium">
                            Parçalı Ödeme Senetlerini Yazdır
                        </label>
                    </div>
                </div>

                {printType === 'partial' && partialInstallmentOptions.length > 0 && (
                    <div className="field p-4 bg-gray-50 dark:bg-gray-700 rounded-xl shadow-sm transition-all duration-300">
                        <label htmlFor="partialInstallments" className="font-bold block mb-3 text-gray-800 dark:text-white border-bottom-1 border-gray-300 dark:border-gray-600 pb-2">
                            Parçalı Ödeme Taksitleri
                        </label>
                        <MultiSelect
                            id="partialInstallments"
                            value={selectedPartialInstallments}
                            options={partialInstallmentOptions}
                            onChange={(e) => setSelectedPartialInstallments(e.value)}
                            optionLabel="label"
                            placeholder="Taksit Seçiniz (Boş bırakılırsa tümü seçilir)"
                            display="chip"
                            className="w-full rounded-lg border-gray-300 dark:border-gray-600 shadow-sm transition-colors duration-200"
                            panelClassName="border-round-xl shadow-lg"
                        />
                    </div>
                )}
            </div>
        </Dialog>
    );
};

SenetPrintDialog.propTypes = {
    visible: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    paymentDetails: PropTypes.array,
    onConfirm: PropTypes.func.isRequired
};

export default SenetPrintDialog;